import { useEffect, useMemo, useState } from 'react'
import {
  ShieldCheck, Users, Layers3, UserRoundCog, RefreshCw, Search,
  Sparkles, LockKeyhole, Network
} from 'lucide-react'
import { api } from '@/lib/api'
import { getToken } from '@/lib/auth'
import { CapabilityControl } from './CapabilityControl'
import { CapabilityPolicyEditor } from './CapabilityPolicyEditor'
import { cn } from '@/lib/utils'

type Directory = {
  subjects: Array<{ principal_id: string; display_name?: string }>
  can_manage_groups: boolean
}

type CentreTab = 'people' | 'roles'

export function CapabilityAdministrationPanel() {
  const [directory, setDirectory] = useState<{ token: string; data: Directory } | null>(null)
  const [selected, setSelected] = useState('')
  const [error, setError] = useState('')
  const [query, setQuery] = useState('')
  const [tab, setTab] = useState<CentreTab>('people')
  const [refreshing, setRefreshing] = useState(false)

  async function loadDirectory() {
    const token = getToken()
    setRefreshing(true)
    try {
      const data = await api.capabilitySubjects()
      if (!Array.isArray(data.subjects)) throw Error('Invalid response')
      if (getToken() !== token) return
      setDirectory({ token, data })
      setError('')
      setSelected(previous => data.subjects.some(s => s.principal_id === previous) ? previous : '')
    } catch {
      if (getToken() === token) {
        setDirectory(null)
        setSelected('')
        setError('Capability administration is unavailable.')
      }
    } finally {
      if (getToken() === token) setRefreshing(false)
    }
  }

  useEffect(() => {
    let active = true
    let generation = 0
    async function refresh() {
      const current = ++generation
      const token = getToken()
      setDirectory(previous => previous?.token === token ? previous : null)
      try {
        const data = await api.capabilitySubjects()
        if (!Array.isArray(data.subjects)) throw Error('Invalid response')
        if (active && current === generation && getToken() === token) {
          setDirectory({ token, data })
          setError('')
          setSelected(previous => data.subjects.some(s => s.principal_id === previous) ? previous : '')
        }
      } catch {
        if (active && current === generation) {
          setDirectory(null)
          setSelected('')
          setError('Capability administration is unavailable.')
        }
      }
    }
    void refresh()
    const listener = () => void refresh()
    for (const event of ['focus', 'ops-auth-change', 'ops-policy-change']) window.addEventListener(event, listener)
    const timer = window.setInterval(listener, 30_000)
    return () => {
      active = false
      generation++
      window.clearInterval(timer)
      for (const event of ['focus', 'ops-auth-change', 'ops-policy-change']) window.removeEventListener(event, listener)
    }
  }, [])

  const data = directory?.token === getToken() ? directory.data : null
  const filtered = useMemo(() => {
    if (!data) return []
    const q = query.trim().toLowerCase()
    return data.subjects.filter(subject =>
      !q ||
      subject.principal_id.toLowerCase().includes(q) ||
      (subject.display_name || '').toLowerCase().includes(q)
    )
  }, [data, query])

  const person = data?.subjects.find(subject => subject.principal_id === selected)
  const ownerCentre = data?.can_manage_groups === true
  const delegatedCentre = data?.can_manage_groups === false

  return (
    <div className="cap-centre">
      <section className="cap-centre-hero">
        <div className="cap-centre-hero-copy">
          <div className="cap-centre-kicker">
            <ShieldCheck className="h-4 w-4" />
            {ownerCentre ? 'Capability Centre' : delegatedCentre ? 'Delegated Capability Control' : 'Capability Control'}
          </div>
          <h1>{ownerCentre ? 'Authority, clearly explained.' : delegatedCentre ? 'Manage only the authority delegated to you.' : 'Loading governed authority…'}</h1>
          <p>
            {ownerCentre
              ? 'See what each person can do, manage role profiles and delegate governed capability administration without mixing capability policy with Worker Lock or approval controls.'
              : delegatedCentre
                ? 'You can view and manage only the people and capabilities explicitly delegated to you. Global role profiles, higher authority and unrelated staff remain outside your scope.'
                : 'Loading your current capability administration scope.'}
          </p>
        </div>
        <div className="cap-centre-hero-orb" aria-hidden="true">
          <ShieldCheck className="h-8 w-8" />
        </div>
      </section>

      <div className="cap-centre-stats">
        <div className="cap-centre-stat">
          <Users className="h-4 w-4" />
          <div><strong>{data?.subjects.length ?? '—'}</strong><span>People in scope</span></div>
        </div>
        <div className="cap-centre-stat">
          <Layers3 className="h-4 w-4" />
          <div>
            <strong>{ownerCentre ? 'Full' : delegatedCentre ? 'Individual only' : '—'}</strong>
            <span>{ownerCentre ? 'Role/profile administration' : 'Delegated capability administration'}</span>
          </div>
        </div>
        <div className="cap-centre-stat">
          <LockKeyhole className="h-4 w-4" />
          <div><strong>Separate</strong><span>Approvals & Worker Lock</span></div>
        </div>
      </div>

      <div className="cap-centre-tabs" role="tablist" aria-label="Capability Centre">
        <button
          role="tab"
          aria-selected={tab === 'people'}
          className={cn('cap-centre-tab', tab === 'people' && 'cap-centre-tab-active')}
          onClick={() => setTab('people')}
        >
          <UserRoundCog className="h-4 w-4" /> People Access
        </button>
        {data?.can_manage_groups && (
          <button
            role="tab"
            aria-selected={tab === 'roles'}
            className={cn('cap-centre-tab', tab === 'roles' && 'cap-centre-tab-active')}
            onClick={() => setTab('roles')}
          >
            <Network className="h-4 w-4" /> Role Profiles
          </button>
        )}
      </div>

      {error && <div role="alert" className="cap-centre-alert cap-centre-alert-error">{error}</div>}

      {tab === 'people' && (
        <div className="cap-centre-grid">
          <aside className="cap-centre-directory">
            <div className="cap-centre-section-head">
              <div>
                <span className="cap-centre-eyebrow">Directory</span>
                <h2>Choose a person</h2>
              </div>
              <button
                className="cap-icon-button"
                onClick={() => void loadDirectory()}
                disabled={refreshing}
                aria-label="Refresh capability directory"
              >
                <RefreshCw className={cn('h-4 w-4', refreshing && 'animate-spin')} />
              </button>
            </div>

            <label className="cap-search">
              <Search className="h-4 w-4" />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search people..."
                aria-label="Search capability subjects"
              />
            </label>

            {!data && !error && (
              <div className="cap-empty-state">
                <Sparkles className="h-5 w-5" />
                <span>Loading assigned people…</span>
              </div>
            )}

            {data && filtered.length === 0 && (
              <div className="cap-empty-state">No people match this view.</div>
            )}

            <div className="cap-person-list">
              {filtered.map(subject => {
                const active = selected === subject.principal_id
                return (
                  <button
                    key={subject.principal_id}
                    onClick={() => setSelected(subject.principal_id)}
                    className={cn('cap-person-card', active && 'cap-person-card-active')}
                  >
                    <span className="cap-person-avatar">
                      {(subject.display_name || subject.principal_id).slice(0, 1).toUpperCase()}
                    </span>
                    <span className="cap-person-copy">
                      <strong>{subject.display_name || subject.principal_id}</strong>
                      <small>{subject.principal_id}</small>
                    </span>
                    <span className="cap-person-state">{active ? 'Viewing' : 'Open'}</span>
                  </button>
                )
              })}
            </div>
          </aside>

          <main className="cap-centre-main">
            {person ? (
              <CapabilityControl key={person.principal_id} principalId={person.principal_id} />
            ) : (
              <div className="cap-centre-placeholder">
                <div className="cap-centre-placeholder-icon"><ShieldCheck className="h-7 w-7" /></div>
                <h2>Select someone to view their authority</h2>
                <p>
                  You’ll see effective capabilities, inherited defaults, individual adjustments and
                  any delegated administration scope without changing anything just by viewing it.
                </p>
              </div>
            )}
          </main>
        </div>
      )}

      {tab === 'roles' && data?.can_manage_groups && (
        <section className="cap-centre-main">
          <div className="cap-centre-section-head mb-4">
            <div>
              <span className="cap-centre-eyebrow">Templates</span>
              <h2>Role and access profiles</h2>
              <p className="text-xs text-text-muted mt-1">
                Default role profiles stay protected. Create a custom access profile when a team needs a different policy.
              </p>
            </div>
          </div>
          <CapabilityPolicyEditor />
        </section>
      )}
    </div>
  )
}
